interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number; // Time window in milliseconds
  lockoutMs: number; // Lockout duration in milliseconds
}

interface RateLimitData {
  attempts: number;
  firstAttempt: number;
  lastAttempt: number;
  lockedUntil?: number;
}

export class RateLimiter {
  private config: RateLimitConfig;
  private storageKey: string;

  constructor(storageKey: string, config: RateLimitConfig) {
    this.storageKey = storageKey;
    this.config = config;
  }

  private getStorageData(): RateLimitData | null {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  private setStorageData(data: RateLimitData): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save rate limit data:', error);
    }
  }

  private clearStorageData(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('Failed to clear rate limit data:', error);
    }
  }

  public canAttempt(): { allowed: boolean; remainingTime?: number; remainingAttempts?: number } {
    const now = Date.now();
    const data = this.getStorageData();

    // If no previous attempts, allow
    if (!data) {
      return { allowed: true, remainingAttempts: this.config.maxAttempts };
    }

    // Check if currently locked out
    if (data.lockedUntil && now < data.lockedUntil) {
      return { 
        allowed: false, 
        remainingTime: data.lockedUntil - now 
      };
    }

    // Check if window has expired
    if (now - data.firstAttempt > this.config.windowMs) {
      this.clearStorageData();
      return { allowed: true, remainingAttempts: this.config.maxAttempts };
    }

    // Check if max attempts reached
    if (data.attempts >= this.config.maxAttempts) {
      const lockoutUntil = data.lastAttempt + this.config.lockoutMs;
      this.setStorageData({
        ...data,
        lockedUntil: lockoutUntil
      });
      return { 
        allowed: false, 
        remainingTime: lockoutUntil - now 
      };
    }

    return { 
      allowed: true, 
      remainingAttempts: this.config.maxAttempts - data.attempts 
    };
  }

  public recordAttempt(): void {
    const now = Date.now();
    const data = this.getStorageData();

    if (!data) {
      this.setStorageData({
        attempts: 1,
        firstAttempt: now,
        lastAttempt: now
      });
    } else {
      this.setStorageData({
        ...data,
        attempts: data.attempts + 1,
        lastAttempt: now
      });
    }
  }

  public reset(): void {
    this.clearStorageData();
  }

  public getRemainingTime(): number | null {
    const data = this.getStorageData();
    if (!data || !data.lockedUntil) return null;
    
    const remaining = data.lockedUntil - Date.now();
    return remaining > 0 ? remaining : null;
  }

  public formatTime(ms: number): string {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }
}

// Pre-configured rate limiters for common use cases
export const forgotPasswordRateLimiter = new RateLimiter('forgot_password_attempts', {
  maxAttempts: 3,
  windowMs: 15 * 60 * 1000, // 15 minutes
  lockoutMs: 30 * 60 * 1000  // 30 minutes
});

export const loginRateLimiter = new RateLimiter('login_attempts', {
  maxAttempts: 5,
  windowMs: 15 * 60 * 1000, // 15 minutes
  lockoutMs: 15 * 60 * 1000  // 15 minutes
}); 